import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { del, patch, post } from "../../lib/api";
import { moveTargets } from "../../lib/holders";
import { useSession } from "../../lib/session";
import type { Holder, HolderDetail, HolderType } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice, Notice } from "../ui/Feedback";
import { Field, SelectField } from "../ui/Input";
import { MicroLabel } from "../ui/Text";
import { HOLDERS_KEY, HolderSelect, useHolders } from "./HolderSelect";

/**
 * Renaming, moving and deleting a holder.
 *
 * Express may be renamed but nothing else: every card defaults to it, and code
 * finds it by id, so it stays a top-level group. The server enforces all of it;
 * the controls simply are not offered.
 *
 * Delete is offered, and the server says why not. Unlike a card, a holder's
 * blockers (cards, subgroups, Apps, recorded sessions) change from the other
 * pages, so the refusal message is the reliable account, not a count read here.
 */
export function EditHolder({ holder }: { readonly holder: HolderDetail }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const holders = useHolders();

  const [name, setName] = useState(holder.name);
  const [type, setType] = useState<HolderType>(holder.type);
  const [externalRef, setExternalRef] = useState(holder.externalRef ?? "");
  const [parent, setParent] = useState(holder.parentHolderId ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saved, setSaved] = useState<string | undefined>(undefined);

  function refresh(text: string) {
    setSaved(text);
    // A rename or move changes what every member's page, card and event says.
    void queryClient.invalidateQueries({ queryKey: ["holder"] });
    void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
    void queryClient.invalidateQueries({ queryKey: ["identities"] });
    void queryClient.invalidateQueries({ queryKey: ["identity"] });
    void queryClient.invalidateQueries({ queryKey: ["events"] });
  }

  const save = useMutation({
    mutationFn: (body: unknown) => patch<Holder>(`/holders/${holder.id}`, body),
    onSuccess: (updated) => refresh(`Saved. This holder is now ${updated.name}.`),
  });

  const move = useMutation({
    mutationFn: (parentHolderId: string | null) =>
      post<Holder>(`/holders/${holder.id}/move`, { parentHolderId }),
    onSuccess: () =>
      refresh("Moved. Its cards now draw on the allowance governing their new place."),
  });

  const remove = useMutation({
    mutationFn: () => del(`/holders/${holder.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
      // Its own policy assignment went with it.
      void queryClient.invalidateQueries({ queryKey: ["allowance-policies"] });
      navigate("/holders", { replace: true });
    },
  });

  if (!can("identities.update") && !can("identities.delete")) {
    return <MicroLabel>Changing a holder needs the Edit cards permission</MicroLabel>;
  }

  const changes = {
    ...(name.trim() === holder.name ? {} : { name: name.trim() }),
    ...(type === holder.type ? {} : { type }),
    ...(externalRef.trim() === (holder.externalRef ?? "") ? {} : { externalRef: externalRef.trim() }),
  };
  const targets = moveTargets(holders.data ?? [], holder.id);
  const error = save.error ?? move.error ?? remove.error;

  /** One outcome on screen at a time: a new attempt clears the others' results. */
  function startOver() {
    setSaved(undefined);
    save.reset();
    move.reset();
    remove.reset();
  }

  return (
    <div className="flex flex-col gap-5">
      {saved === undefined ? null : <Notice tone="ok">{saved}</Notice>}
      {error === null ? null : <ErrorNotice error={error} />}

      {can("identities.update") ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (Object.keys(changes).length === 0 || name.trim() === "") return;
            startOver();
            save.mutate(changes);
          }}
          className="grid grid-cols-1 items-end gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-4"
        >
          <Field
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            required
          />
          <SelectField
            label="Type"
            value={type}
            disabled={holder.isSystem}
            onChange={(event) => setType(event.target.value as HolderType)}
            hint="A group with members, or one an App acts for, stays a group."
          >
            <option value="group">Group</option>
            <option value="person">Person</option>
          </SelectField>
          <Field
            label="Reference"
            value={externalRef}
            onChange={(event) => setExternalRef(event.target.value)}
            maxLength={200}
            spellCheck={false}
            autoComplete="off"
            hint="Optional label, such as an employee or cost-centre number."
          />
          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={Object.keys(changes).length === 0 || name.trim() === ""}
              pending={save.isPending}
            >
              Save
            </Button>
          </div>
        </form>
      ) : null}

      {can("identities.update") && !holder.isSystem ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            startOver();
            move.mutate(parent === "" ? null : parent);
          }}
          className="grid grid-cols-1 items-end gap-x-6 gap-y-5 border-t border-rule pt-4 md:grid-cols-[2fr_auto]"
        >
          <HolderSelect
            label="Inside"
            value={parent}
            onChange={setParent}
            options={targets}
            emptyLabel="Top level"
            hint="Its cards and subgroups move with it, into whichever pool governs the new place."
          />
          <div>
            <Button
              type="submit"
              disabled={parent === (holder.parentHolderId ?? "")}
              pending={move.isPending}
            >
              Move
            </Button>
          </div>
        </form>
      ) : null}

      {can("identities.delete") && !holder.isSystem ? (
        <div className="flex items-center gap-3 border-t border-rule pt-4">
          {confirmingDelete ? (
            <>
              <Button
                variant="danger"
                pending={remove.isPending}
                onClick={() => {
                  setConfirmingDelete(false);
                  startOver();
                  remove.mutate();
                }}
              >
                Confirm delete
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
              Delete this holder
            </Button>
          )}
          <span className="text-micro tracking-normal normal-case text-ink-faint">
            Only an empty holder with no recorded history can be deleted.
          </span>
        </div>
      ) : null}
    </div>
  );
}
